import { createElement, useState } from 'react';

import type { DatabaseView, DatabaseViewConfig, DatabaseViewType } from '@/lib/databases/view-config';
import {
  addView,
  deleteView,
  duplicateView,
  renameView,
  setActiveViewId,
} from '@couplespace/core/databases';
import { createViewId } from '@/lib/databases/view-config';
import { stopBubbleUnlessFormField } from '@/lib/databases/stop-editor-bubble';
import { anchorBelowElement, Menu, MenuItem } from '@/components/ui/Menu.web';

import type { DatabaseColumnDefinition } from '@/lib/databases/types';

import { iconForViewType } from './DatabaseMenuIcons.web';

type DatabaseViewTabsProps = {
  viewConfig: DatabaseViewConfig;
  columns: DatabaseColumnDefinition[];
  disabled?: boolean;
  onChange: (config: DatabaseViewConfig, options?: { immediate?: boolean }) => void;
};

type TabMenuState = { viewId: string } | null;
type AddPanelState = 'open' | null;

function firstSelectColumnId(columns: DatabaseColumnDefinition[]): string | undefined {
  return columns.find((c) => c.type === 'select')?.id;
}

function firstDateColumnId(columns: DatabaseColumnDefinition[]): string | undefined {
  return columns.find((c) => c.type === 'date')?.id;
}

function stopBubble(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

export function DatabaseViewTabs({
  viewConfig,
  columns,
  disabled,
  onChange,
}: DatabaseViewTabsProps) {
  const [tabMenu, setTabMenu] = useState<TabMenuState>(null);
  const [tabMenuAnchor, setTabMenuAnchor] = useState<ReturnType<typeof anchorBelowElement> | null>(
    null,
  );
  const [addPanel, setAddPanel] = useState<AddPanelState>(null);
  const [addAnchor, setAddAnchor] = useState<ReturnType<typeof anchorBelowElement> | null>(null);
  const [newViewName, setNewViewName] = useState('');
  const [newViewType, setNewViewType] = useState<DatabaseViewType>('table');
  const [renameDraft, setRenameDraft] = useState('');

  function switchView(viewId: string) {
    onChange(setActiveViewId(viewConfig, viewId), { immediate: true });
  }

  function openTabMenu(viewId: string, el: HTMLElement) {
    const view = viewConfig.views.find((v) => v.id === viewId);
    setRenameDraft(view?.name ?? '');
    setTabMenu({ viewId });
    setTabMenuAnchor(anchorBelowElement(el));
  }

  function closeTabMenu() {
    setTabMenu(null);
    setTabMenuAnchor(null);
  }

  function commitRename() {
    if (!tabMenu) {
      return;
    }
    onChange(renameView(viewConfig, tabMenu.viewId, renameDraft), { immediate: true });
    closeTabMenu();
  }

  function handleDuplicate() {
    if (!tabMenu) {
      return;
    }
    onChange(duplicateView(viewConfig, tabMenu.viewId, createViewId), { immediate: true });
    closeTabMenu();
  }

  function handleDelete() {
    if (!tabMenu) {
      return;
    }
    onChange(deleteView(viewConfig, tabMenu.viewId), { immediate: true });
    closeTabMenu();
  }

  function openAddPanel(el: HTMLElement) {
    setNewViewName('');
    setNewViewType('table');
    setAddPanel('open');
    setAddAnchor(anchorBelowElement(el));
  }

  function closeAddPanel() {
    setAddPanel(null);
    setAddAnchor(null);
  }

  function commitAddView() {
    if (
      newViewType !== 'table' &&
      newViewType !== 'board' &&
      newViewType !== 'list' &&
      newViewType !== 'calendar' &&
      newViewType !== 'timeline'
    ) {
      return;
    }
    const defaultNames: Record<string, string> = {
      table: 'Table',
      board: 'Board',
      list: 'List',
      calendar: 'Calendar',
      timeline: 'Timeline',
    };
    const name = newViewName.trim() || defaultNames[newViewType] || 'View';
    const boardColumnId =
      newViewType === 'board' ? firstSelectColumnId(columns) : undefined;
    const calendarColumnId =
      newViewType === 'calendar' ? firstDateColumnId(columns) : undefined;
    const timelineColumnId =
      newViewType === 'timeline' ? firstDateColumnId(columns) : undefined;
    const view: Omit<DatabaseView, 'id'> = {
      name,
      type: newViewType,
      filters: [],
      sorts: [],
      groupBy: null,
      boardColumnId,
      calendarColumnId,
      timelineColumnId,
    };
    onChange(addView(viewConfig, view, createViewId), { immediate: true });
    closeAddPanel();
  }

  return createElement(
    'div',
    {
      className: 'cs-db-view-tabs',
      onMouseDown: stopBubble,
      onKeyDownCapture: stopBubbleUnlessFormField,
    },
    createElement(
      'div',
      { className: 'cs-db-view-tabs-list' },
      ...viewConfig.views.map((view) => {
        const active = view.id === viewConfig.activeViewId;
        return createElement(
          'div',
          {
            key: view.id,
            className: active ? 'cs-db-view-tab cs-db-view-tab-active' : 'cs-db-view-tab',
          },
          createElement(
            'button',
            {
              type: 'button',
              className: 'cs-db-view-tab-btn',
              disabled,
              onClick: () => switchView(view.id),
              onMouseDown: stopBubble,
            },
            createElement('span', { className: 'cs-db-view-tab-icon' }, iconForViewType(view.type)),
            createElement('span', { className: 'cs-db-view-tab-name' }, view.name),
          ),
          createElement(
            'button',
            {
              type: 'button',
              className: 'cs-db-view-tab-menu-btn',
              disabled,
              'aria-label': `View options for ${view.name}`,
              onClick: (e: MouseEvent) => {
                stopBubble(e);
                openTabMenu(view.id, e.currentTarget as HTMLElement);
              },
              onMouseDown: stopBubble,
            },
            '⋯',
          ),
        );
      }),
      createElement(
        'button',
        {
          type: 'button',
          className: 'cs-db-view-tab-add',
          disabled,
          title: 'Add view',
          'aria-label': 'Add view',
          onClick: (e: MouseEvent) => {
            stopBubble(e);
            openAddPanel(e.currentTarget as HTMLElement);
          },
          onMouseDown: stopBubble,
        },
        '+',
      ),
    ),
    createElement(Menu, {
      open: tabMenu !== null,
      anchor: tabMenuAnchor,
      onClose: closeTabMenu,
      minWidth: 240,
      children:
        tabMenu === null
          ? null
          : createElement(
              'div',
              { className: 'cs-db-view-tab-menu' },
              createElement('label', { className: 'cs-db-popover-label' }, 'Rename'),
              createElement('input', {
                type: 'text',
                className: 'cs-db-popover-input',
                value: renameDraft,
                onChange: (e: Event) => setRenameDraft((e.target as HTMLInputElement).value),
                onKeyDown: (e: KeyboardEvent) => {
                  stopBubble(e);
                  if (e.key === 'Enter') {
                    commitRename();
                  }
                },
                onMouseDown: stopBubble,
              }),
              createElement(
                'button',
                { type: 'button', className: 'cs-db-popover-btn', onClick: () => commitRename() },
                'Save name',
              ),
              createElement(
                'button',
                { type: 'button', className: 'cs-db-popover-btn', onClick: () => handleDuplicate() },
                'Duplicate',
              ),
              viewConfig.views.length > 1
                ? createElement(
                    'button',
                    {
                      type: 'button',
                      className: 'cs-db-popover-btn cs-db-popover-btn-danger',
                      onClick: () => handleDelete(),
                    },
                    'Delete view',
                  )
                : null,
            ),
    }),
    createElement(Menu, {
      open: addPanel === 'open',
      anchor: addAnchor,
      onClose: closeAddPanel,
      minWidth: 260,
      children: createElement(
        'div',
        { className: 'cs-db-view-add-panel' },
        createElement('label', { className: 'cs-db-popover-label' }, 'Name'),
        createElement('input', {
          type: 'text',
          className: 'cs-db-popover-input',
          placeholder: 'View name',
          value: newViewName,
          onChange: (e: Event) => setNewViewName((e.target as HTMLInputElement).value),
          onMouseDown: stopBubble,
        }),
        createElement('label', { className: 'cs-db-popover-label' }, 'Type'),
        createElement(
          'select',
          {
            className: 'cs-db-popover-select',
            value: newViewType,
            onChange: (e: Event) =>
              setNewViewType((e.target as HTMLSelectElement).value as DatabaseViewType),
            onMouseDown: stopBubble,
          },
          createElement('option', { value: 'table' }, 'Table'),
          createElement('option', { value: 'list' }, 'List'),
          createElement('option', { value: 'board' }, 'Board'),
          createElement('option', { value: 'calendar' }, 'Calendar'),
          createElement('option', { value: 'timeline' }, 'Timeline'),
        ),
        newViewType === 'board'
          ? createElement(
              'p',
              { className: 'cs-db-popover-hint' },
              firstSelectColumnId(columns)
                ? 'Uses your first Select column for lanes.'
                : 'You will be prompted to add a Select column (e.g. Status).',
            )
          : newViewType === 'calendar' || newViewType === 'timeline'
            ? createElement(
                'p',
                { className: 'cs-db-popover-hint' },
                firstDateColumnId(columns)
                  ? 'Uses your first Date column (ranges supported).'
                  : 'You will be prompted to add a Date column.',
              )
            : null,
        MenuItem({
          label: 'Create view',
          onPress: () => commitAddView(),
        }),
      ),
    }),
  );
}
